// Multiplayer milestones: no pause, no camera — a short toast under the
// player strip that goes away on its own (TOAST_MS) or on click.

import { useEffect, useSyncExternalStore } from 'react';
import { useT } from '../i18n/index.js';
import { appDom } from '../lib/appDom.js';
import { milestoneScene } from '../ui/sceneEvents.js';
import { sceneUrls } from '../ui/scenes.js';
import { MILESTONE_ICONS } from './CinematicOverlay.js';
import { milestoneCopy } from './copy.js';
import { type CinematicDirector, type MilestoneToast, TOAST_MS } from './director.js';

export function MilestoneToastStack({
  director,
  localPlayer,
}: {
  readonly director: CinematicDirector;
  readonly localPlayer: number;
}) {
  const { toasts } = useSyncExternalStore(director.subscribe, director.getState, director.getState);
  if (toasts.length === 0) return null;
  return (
    <div className="pointer-events-none fixed inset-x-0 top-[calc(var(--hud-panel-top)+0.5rem)] z-50 flex flex-col items-center gap-2 px-3">
      {toasts.slice(-3).map((toast) => (
        <Toast key={toast.id} toast={toast} director={director} localPlayer={localPlayer} />
      ))}
    </div>
  );
}

function Toast({
  toast,
  director,
  localPlayer,
}: {
  readonly toast: MilestoneToast;
  readonly director: CinematicDirector;
  readonly localPlayer: number;
}) {
  const t = useT();
  useEffect(() => {
    const id = setTimeout(() => director.dismissToast(toast.id), TOAST_MS);
    return () => clearTimeout(id);
  }, [director, toast.id]);
  const copy = milestoneCopy(toast.milestone, t, localPlayer);
  const Icon = MILESTONE_ICONS[copy.icon];
  const kid = toast.uiMode === 'icons';
  // The milestone's picture (art audit P2), when its family has one; the
  // icon stands in otherwise.
  const scene = milestoneScene(toast.milestone);
  return (
    <button
      type="button"
      data-testid="cinematic-toast"
      {...appDom({ id: 'cinematic-toast', type: 'Toast', scope: 'toast' })}
      aria-label={t('cinematic.toastLabel')}
      onClick={() => director.dismissToast(toast.id)}
      className="animate-slide-up pointer-events-auto flex max-w-[min(92vw,420px)] items-center gap-3 rounded-xl border border-amber-500/50 bg-panel-scrim px-4 py-2.5 text-left text-foreground shadow-[0_0_24px_rgba(255,180,80,0.16)] backdrop-blur-sm dark:shadow-[0_0_30px_rgba(255,180,80,0.25)]"
    >
      {scene ? (
        <img
          src={sceneUrls(scene.group, scene.id).srcSmall}
          alt=""
          loading="lazy"
          className={`${kid ? 'h-14 w-24' : 'h-10 w-[4.5rem]'} shrink-0 rounded-md border border-amber-500/30 object-cover`}
          data-testid="milestone-scene"
        />
      ) : (
        <Icon
          className={`${kid ? 'size-8' : 'size-5'} shrink-0 text-amber-600 dark:text-amber-400`}
          aria-hidden="true"
        />
      )}
      <span className="flex min-w-0 flex-col">
        <span
          className={`font-serif leading-tight text-amber-700 dark:text-amber-300 ${kid ? 'text-lg' : 'text-sm'}`}
        >
          {copy.title}
        </span>
        <span className="truncate text-xs text-muted-foreground">
          {kid ? copy.kidLine : copy.subtitle}
        </span>
      </span>
    </button>
  );
}
